import React, { useLayoutEffect, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './WayfindingProject.css';

gsap.registerPlugin(ScrollTrigger);

const W = '/content/wayfinding/';

interface WayfindingProjectProps {
  theme?: string;
}

const WayfindingProject: React.FC<WayfindingProjectProps> = ({ theme = 'light' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      gsap.from('.wp__hero-img', {
        y: 70, opacity: 0, duration: 1.5, ease: 'power3.out', delay: 0.1
      });
      gsap.from('.wp__title', {
        y: 32, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.25
      });
      gsap.from('.wp__descriptor', {
        y: 22, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.4
      });
      gsap.from('.wp__meta-item', {
        y: 16, opacity: 0, duration: 0.65, ease: 'power3.out', stagger: 0.07, delay: 0.55
      });

      gsap.set('.wp-fade-up', { y: 30, opacity: 0 });
      gsap.set('.wp-fade-left', { x: -52, opacity: 0 });
      gsap.set('.wp-fade-right', { x: 52, opacity: 0 });
      gsap.set('.wp-fade-scale', { scale: 0.93, opacity: 0 });

      ScrollTrigger.batch('.wp-fade-up', {
        onEnter: (els) => gsap.to(els, {
          y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out'
        }),
        start: 'top 90%',
        once: true,
      });

      ScrollTrigger.batch('.wp-fade-left', {
        onEnter: (els) => gsap.to(els, {
          x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

      ScrollTrigger.batch('.wp-fade-right', {
        onEnter: (els) => gsap.to(els, {
          x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

      ScrollTrigger.batch('.wp-fade-scale', {
        onEnter: (els) => gsap.to(els, {
          scale: 1, opacity: 1, stagger: 0.08, duration: 1, ease: 'power3.out'
        }),
        start: 'top 88%',
        once: true,
      });

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="wp" ref={rootRef}>

      {/* Hero */}
      <section className="wp__hero">
        <img
          className="wp__hero-img"
          src={`${W}iPhone 15 Pro Mockup.png`}
          alt="Traversor app on iPhone"
        />
      </section>

      <div className="wp__container">

        {/* Header */}
        <header className="wp__header">
          <h1 className="wp__title">Traversor</h1>
          <p className="wp__descriptor">
            A tightly integrated kiosk and mobile web app ecosystem with AR and beacon support to guide users across downtown Indianapolis without requiring an installed app.
          </p>
          <div className="wp__meta">
            {[
              { label: 'Type', value: 'UX Design' },
              { label: 'Year', value: '2025' },
              { label: 'Role', value: 'UX Researcher & Designer' },
              { label: 'Tools', value: 'Figma, Blender' },
            ].map(({ label, value }) => (
              <div className="wp__meta-item" key={label}>
                <div className="wp__meta-label">{label}</div>
                <div className="wp__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        {/* TL;DR */}
        <section className="wp__section">
          <div className="wp__section-label wp-fade-up">tl;dr</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              Downtown Indianapolis has no unified navigation layer. Traversor bridges kiosks, beacons, and mobile web to close that gap without requiring an installed app.
            </p>
            <div>
              <p className="wp__body wp-fade-up">
                We designed Traversor for a graduate urban design course, targeting the fragmented experience visitors face navigating downtown Indianapolis. The system connects physical kiosks to a mobile web app via QR and Bluetooth, so users can discover destinations at a kiosk and continue seamlessly on their phone.
              </p>
              <div className="wp__outcomes">
                <div className="wp__outcome-item wp-fade-up">
                  <div className="wp__outcome-num">2</div>
                  <div className="wp__outcome-desc">Rounds of usability testing with actionable insights each</div>
                </div>
                <div className="wp__outcome-item wp-fade-up">
                  <div className="wp__outcome-num">4</div>
                  <div className="wp__outcome-desc">Strategic kiosk locations forming a navigation quadrilateral</div>
                </div>
                <div className="wp__outcome-item wp-fade-up">
                  <div className="wp__outcome-num">6</div>
                  <div className="wp__outcome-desc">Core features across kiosk and mobile web platforms</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Challenge */}
        <section className="wp__section">
          <div className="wp__section-label wp-fade-up">the challenge</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              Fragmented tools, zero coordination. Every user group navigates the same broken system.
            </p>
            <p className="wp__body wp-fade-up">
              Navigating downtown Indianapolis is confusing and inconsistent for locals and visitors alike. Scattered tools, disconnected apps, and static maps fail to create a cohesive experience. Different user groups, from first-time visitors to locals discovering hidden gems, face the same disjointed infrastructure that does not adapt to their needs.
            </p>
          </div>
          <div className="wp__problem-strip">
            {[
              'Visitors rely on scattered tools like maps, apps, or outdated signage with no unified, real-time navigation experience.',
              'Existing systems fail to adapt to different user needs, from locals seeking hidden gems to first-timers needing step-by-step guidance.',
              'Minimal connection between physical kiosks and digital platforms creates disjointed experiences that interrupt spatial understanding.',
            ].map((text, i) => (
              <div className="wp__problem-item wp-fade-up" key={i}>
                <span className="wp__problem-num">{String(i + 1).padStart(2, '0')}</span>
                <p className="wp__body">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Research intro */}
        <section className="wp__section wp__section--no-bottom">
          <div className="wp__section-label wp-fade-up">research</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              We studied how the best wayfinding systems turn urban complexity into effortless clarity.
            </p>
            <p className="wp__body wp-fade-up">
              We began by studying global wayfinding exemplars, including airports, office campuses, and urban kiosks, focusing on how physical navigation experiences translate into intuitive digital interfaces and AR overlays.
            </p>
          </div>
        </section>

      </div>

      {/* Moodboard full bleed */}
      <div className="wp__moodboard-strip">
        {[5, 6, 7, 8].map((n, i) => (
          <img
            key={i}
            className="wp__moodboard-img wp-fade-scale"
            src={`${W}moodboard/Traversor Presentation (${n}).png`}
            alt={`Research reference ${i + 1}`}
          />
        ))}
      </div>

      <div className="wp__container">

        {/* Research insights */}
        <div className="wp__insights-strip">
          {[
            { num: '01', title: 'Visual Hierarchy', text: 'Color-coding and visual hierarchy significantly improve user comprehension in complex navigation spaces.' },
            { num: '02', title: 'Seamless Transitions', text: 'Physical and digital touchpoint integration reduces cognitive load during navigation.' },
            { num: '03', title: 'Familiar Paradigms', text: 'AR overlays work best when they mirror familiar physical signage rather than introducing new patterns.' },
            { num: '04', title: 'Aesthetic Balance', text: 'Successful systems balance aesthetic appeal with functional clarity, making navigation feel effortless.' },
            { num: '05', title: 'Multi-Modal', text: 'Combined kiosks, mobile, and signage serve diverse user preferences and contexts.' },
          ].map(({ num, title, text }) => (
            <div className="wp__insight-item wp-fade-up" key={num}>
              <span className="wp__insight-num">{num}</span>
              <p className="wp__insight-title">{title}</p>
              <p className="wp__body-small">{text}</p>
            </div>
          ))}
        </div>

        {/* Field Observations intro */}
        <section className="wp__section wp__section--no-bottom">
          <div className="wp__section-label wp-fade-up">field observations</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              We walked the city ourselves before drawing a single screen.
            </p>
            <p className="wp__body wp-fade-up">
              Our team went across downtown to shadow visitors, document pain points, and scout strategic locations for kiosk placement. We observed six sites capturing the full range of navigation challenges, from dense attraction clusters to transit connection points.
            </p>
          </div>
        </section>

      </div>

      {/* Field observation photo grid full bleed */}
      <div className="wp__obs-grid">
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <img
            key={n}
            className="wp__obs-img wp-fade-scale"
            src={`${W}obseravtion/Traversor Presentation${n === 0 ? '' : ` (${n})`}.jpg`}
            alt={`Field observation ${n + 1}`}
          />
        ))}
      </div>

      <div className="wp__container">

        {/* Understanding Users intro */}
        <section className="wp__section wp__section--no-bottom">
          <div className="wp__section-label wp-fade-up">understanding users</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              Three personas, three different strategies for navigating an unfamiliar city.
            </p>
            <p className="wp__body wp-fade-up">
              We created personas and scenarios to step into user shoes during design and testing. Understanding the diversity of navigation styles, familiarity levels, and exploration goals shaped every feature priority we set.
            </p>
          </div>
        </section>

      </div>

      {/* Persona slides */}
      <div className="wp__personas-strip">
        {[
          `${W}personas/Wayfinding System Design May 9 2025.png`,
          `${W}personas/Wayfinding System Design May 9 2025 (1).png`,
          `${W}personas/Wayfinding System Design May 9 2025 from Traversor.png`,
        ].map((src, i) => (
          <img
            key={i}
            className="wp__persona-img wp-fade-scale"
            src={src}
            alt={`User persona ${i + 1}`}
          />
        ))}
      </div>

      <div className="wp__container">

        {/* Key Findings */}
        <section className="wp__section">
          <div className="wp__section-label wp-fade-up">key findings</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              Four patterns surfaced across all our research and directly shaped every design decision.
            </p>
            <p className="wp__body wp-fade-up">
              Field observations and secondary research converged on a consistent set of navigation behaviors. These findings informed the feature set, the kiosk-to-mobile handoff model, and the visual language of the AR overlay.
            </p>
          </div>
          <div className="wp__findings-strip">
            {[
              'Users follow landmarks over directions. "Next to Starbucks" is more useful than "walk 300 ft."',
              'Color-coded visual paths are more memorable than text-based routes.',
              'Phones first, kiosks second. Most users default to mobile and only seek kiosks when lost.',
              'Different users use different navigation strategies: mental mapping vs. route-based.',
            ].map((text, i) => (
              <div className="wp__finding-item wp-fade-up" key={i}>
                <span className="wp__finding-num">{String(i + 1).padStart(2, '0')}</span>
                <p className="wp__body">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Lo-Fi Prototypes */}
        <section className="wp__section">
          <div className="wp__section-label wp-fade-up">lo-fi prototypes</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              One unified design language across kiosk and mobile, built around the handoff.
            </p>
            <div>
              <p className="wp__body wp-fade-up">
                Our low-fidelity prototypes established a shared design language across both the physical kiosk and mobile web app, prioritizing next-kiosk navigation, AR wayfinding, and curated destination discovery.
              </p>
              <p className="wp__body wp-fade-up" style={{ marginTop: '1rem' }}>
                The kiosk-to-mobile handoff became the cornerstone feature: users discover destinations at the kiosk, scan a QR code, and pick up navigation on their own device with no install required.
              </p>
            </div>
          </div>
          <div className="wp__lofi-grid">
            {[
              `${W}lowfi/Image Background Remover.png`,
              `${W}lowfi/Image Background Remover (1).png`,
              `${W}lowfi/Image Background Remover (2).png`,
            ].map((src, i) => (
              <img
                key={i}
                className="wp__lofi-img wp-fade-scale"
                src={src}
                alt={`Lo-fi prototype ${i + 1}`}
              />
            ))}
          </div>
        </section>

      </div>

      {/* Solution dark banner */}
      <section className="wp__solution-banner">
        <div className="wp__container">
          <div className="wp__section-label wp-fade-up" style={{ color: 'rgba(255,255,255,0.35)' }}>the solution</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up" style={{ color: 'rgba(255,255,255,0.9)' }}>
              Traversor: connected kiosk and mobile, guided by AR, powered by beacons.
            </p>
            <p className="wp__body wp-fade-up" style={{ color: 'rgba(255,255,255,0.6)' }}>
              A tightly integrated system guiding users across downtown Indianapolis without requiring an installed app. Every feature was shaped by what we learned from research, observation, and testing.
            </p>
          </div>
        </div>
      </section>

      <div className="wp__container">

        {/* Feature pairs */}
        <div className="wp__feature-pair">
          <div className="wp__feature-content wp-fade-left">
            <h3 className="wp__feature-title">No Clutter</h3>
            <p className="wp__body">Straight to work. Clean menu with language options at the start to avoid usability friction before the user even begins exploring.</p>
          </div>
          <img
            className="wp__feature-img wp-fade-right"
            src={`${W}imple 2/noclutter.png`}
            alt="No Clutter interface"
          />
        </div>

        <div className="wp__feature-pair wp__feature-pair--flip">
          <div className="wp__feature-content wp-fade-right">
            <h3 className="wp__feature-title">Memories On the Go</h3>
            <p className="wp__body">Kiosks placed at photogenic spots eliminate the need for a dedicated photographer. Pictures transfer instantly to your phone via QR code.</p>
          </div>
          <img
            className="wp__feature-img wp-fade-left"
            src={`${W}imple 2/memories on the go.png`}
            alt="Memories on the Go"
          />
        </div>

        <div className="wp__feature-pair">
          <div className="wp__feature-content wp-fade-left">
            <h3 className="wp__feature-title">We Didn't Leave Anyone Out</h3>
            <p className="wp__body">Multi-language support built in from day one, with all major languages available and more on the way, so every visitor feels at home navigating the city.</p>
          </div>
          <img
            className="wp__feature-img wp-fade-right"
            src={`${W}imple 2/didntleaveanyone.png`}
            alt="Language support"
          />
        </div>

        <div className="wp__feature-pair wp__feature-pair--flip">
          <div className="wp__feature-content wp-fade-right">
            <h3 className="wp__feature-title">Smooth Transition</h3>
            <p className="wp__body">Scan the QR code on any kiosk to pick up exactly where you left off on your phone. No app to install, no account needed.</p>
          </div>
          <div className="wp__feature-dual wp-fade-left">
            <img src={`${W}imple 2/smooth trans1.png`} alt="Kiosk QR handoff" />
            <img src={`${W}imple 2/smooth trans2.png`} alt="Mobile continuation" />
          </div>
        </div>

        <div className="wp__feature-pair">
          <div className="wp__feature-content wp-fade-left">
            <h3 className="wp__feature-title">Explore Around</h3>
            <p className="wp__body">The web app surfaces nearby cafes, restaurants, and entertainment so you can discover more without breaking out of the navigation flow.</p>
          </div>
          <div className="wp__feature-dual wp-fade-right">
            <img src={`${W}imple 2/splash screen.png`} alt="Splash screen" />
            <img src={`${W}imple 2/nav.png`} alt="Navigation view" />
          </div>
        </div>

        <div className="wp__feature-pair wp__feature-pair--flip">
          <div className="wp__feature-content wp-fade-right">
            <h3 className="wp__feature-title">AR Navigation</h3>
            <p className="wp__body">Inspired by physical signage, the AR module overlays virtual signboards and highlights nearby locations so you can explore without looking down at a map.</p>
          </div>
          <img
            className="wp__feature-img wp-fade-left"
            src={`${W}imple 2/ar.png`}
            alt="AR navigation overlay"
          />
        </div>

        {/* How It Works */}
        <section className="wp__section">
          <div className="wp__section-label wp-fade-up">how it works</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              Two ways in, four anchor locations, one system that hands off seamlessly.
            </p>
            <p className="wp__body wp-fade-up">
              Kiosks are placed at four strategic downtown locations forming a quadrilateral that encloses the heart of Indianapolis. Users can enter the system from any kiosk or directly from the web app on their phone.
            </p>
          </div>

          <div className="wp__kiosk-strip">
            {[
              'Massachusetts Avenue',
              'White River State Park',
              'Monument Circle',
              'Gainbridge Fieldhouse',
            ].map((name, i) => (
              <div className="wp__kiosk-item wp-fade-up" key={i}>
                <span className="wp__kiosk-num">{String(i + 1).padStart(2, '0')}</span>
                <p className="wp__kiosk-name">{name}</p>
              </div>
            ))}
          </div>

          <div className="wp__flow-grid">
            <div className="wp__flow-col wp-fade-up">
              <h3 className="wp__flow-title">Direct Web Access</h3>
              <p className="wp__body">Access the web app from anywhere to plan your downtown exploration before or during your visit.</p>
              <ol className="wp__flow-list">
                {[
                  'Visit the Traversor web app on your phone',
                  'Browse popular places or search for a destination',
                  'Select your mode of travel',
                  'Navigate with AR overlay or map view',
                ].map((step, i) => (
                  <li key={i} className="wp-fade-up">{step}</li>
                ))}
              </ol>
            </div>
            <div className="wp__flow-col wp-fade-up">
              <h3 className="wp__flow-title">Kiosk to Mobile</h3>
              <p className="wp__body">Scan a kiosk QR code to automatically load your current location and continue navigation on your device.</p>
              <ol className="wp__flow-list">
                {[
                  'Find a Traversor kiosk at key downtown locations',
                  'Scan the QR code with your phone camera',
                  'Web app opens with your kiosk location pre-loaded',
                  'Select destination and begin navigation from your current spot',
                ].map((step, i) => (
                  <li key={i} className="wp-fade-up">{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* Reflections */}
        <section className="wp__section">
          <div className="wp__section-label wp-fade-up">reflections</div>
          <div className="wp__split">
            <p className="wp__statement wp-fade-up">
              What the constraints taught us, and where Traversor goes next.
            </p>
            <div>
              <div className="wp__closing-block wp-fade-up">
                <p className="wp__closing-sub">Limitations</p>
                <p className="wp__body">Current prototypes use cardboard and 3D renders rather than the envisioned stainless steel kiosk construction, limiting real-world durability testing. The web app requires modern smartphones with camera access for AR and QR features. Usability testing was conducted with a limited sample primarily of university students.</p>
              </div>
              <div className="wp__closing-block wp-fade-up">
                <p className="wp__closing-sub">What's Next</p>
                <p className="wp__body">Expand the kiosk network beyond the initial four locations. Add community reviews and ratings for destinations. Introduce audio navigation for vehicle users. Explore in-app and kiosk ticket booking for events and attractions to reduce friction in the visitor journey.</p>
              </div>
            </div>
          </div>
        </section>

      </div>

    </div>
  );
};

export default WayfindingProject;
